import { Coffee, Cookie, Croissant, Cake, Sparkles } from "lucide-react";

const sections = [
    {
        id: "cafe",
        eyebrow: "Une pause sur place",
        title: "On vous sert un café.",
        scriptPart: " Et un goûter.",
        text: "Le bon prétexte pour s'installer en boutique. On a le café, le moment, et ce qu'il faut autour. Vous repartez plus tard, promis.",
        icon: Coffee,
        img: "/images/gouter-cafe.jpg",
        imgAlt: "Tasse de café noir, l'allié des pauses gourmandes",
        accent: "from-arl-blue/15 via-arl-cream to-arl-sun/15",
        iconColor: "text-arl-blue/55",
        chips: [
            { label: "Café", icon: Coffee },
            { label: "Boissons chaudes" },
        ],
    },
    {
        id: "gateaux",
        eyebrow: "Faits maison",
        title: "Les gâteaux,",
        scriptPart: " sortis du four.",
        text: "Cookies tout juste cuits, gaufres dorées à la minute, financiers, pâtisseries du jour. La maison fait aussi ses propres gâteaux glacés et entremets, à composer en boutique.",
        icon: Cookie,
        img: "/images/gateaux.webp",
        imgAlt: "Gaufres dorées tout juste cuites chez Arlequin",
        accent: "from-arl-rose/15 via-arl-cream to-arl-coral/15",
        iconColor: "text-arl-rose/65",
        chips: [
            { label: "Cookies", icon: Cookie },
            { label: "Gaufres", icon: Croissant },
            { label: "Gâteaux glacés", icon: Cake },
        ],
    },
    {
        id: "vanille",
        eyebrow: "Notre étagère",
        title: "Les gousses",
        scriptPart: " de vanille.",
        text: "Gousses de Madagascar vendues à l'unité, à infuser dans une crème ou un caramel à la maison. Au gré de l'étagère, vous trouverez aussi quelques trouvailles d'épicerie sucrée.",
        icon: Sparkles,
        img: "/images/gouter-vanille.jpg",
        imgAlt: "Bouquet de gousses de vanille de Madagascar",
        accent: "from-arl-sun/25 via-arl-cream to-arl-mint/20",
        iconColor: "text-arl-orange/60",
        chips: [
            { label: "Vendues à l'unité" },
            { label: "Origine Madagascar" },
        ],
    },
];

function PhotoPlaceholder({ Icon, accent, iconColor, alt }) {
    return (
        <div
            className={`relative aspect-[4/5] rounded-[1.8rem] overflow-hidden border border-arl-blue/10 bg-gradient-to-br ${accent} flex items-center justify-center shadow-lg`}
            role="img"
            aria-label={alt}
        >
            <div className="absolute inset-0 rhombus-bg opacity-50 pointer-events-none" />
            <div className="relative text-center px-6">
                <Icon size={56} className={`${iconColor} mx-auto mb-4`} strokeWidth={1.4} />
                <p className="font-script text-3xl text-arl-blue/70 leading-none">Photo à venir</p>
            </div>
        </div>
    );
}

function SectionVisual({ section }) {
    if (section.img) {
        return (
            <div className="relative">
                <img
                    src={section.img}
                    alt={section.imgAlt}
                    className="w-full aspect-[4/5] object-cover rounded-[1.8rem] shadow-xl"
                    data-testid={`gouter-image-${section.id}`}
                />
            </div>
        );
    }
    return (
        <PhotoPlaceholder
            Icon={section.icon}
            accent={section.accent}
            iconColor={section.iconColor}
            alt={`${section.title} ${section.scriptPart}`}
        />
    );
}

function GouterSection({ section, index }) {
    const reversed = index % 2 === 1;
    return (
        <section
            id={section.id}
            data-testid={`gouter-section-${section.id}`}
            className="relative py-16 sm:py-20"
        >
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}>
                        <SectionVisual section={section} />
                    </div>

                    <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
                        <p className="text-xs uppercase tracking-[0.24em] text-arl-blue/70 font-semibold">
                            {section.eyebrow}
                        </p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl text-arl-ink leading-tight">
                            {section.title}
                            <span className="font-script text-arl-blue">{section.scriptPart}</span>
                        </h2>
                        <p className="mt-5 text-lg text-arl-ink/75 leading-relaxed">{section.text}</p>

                        {section.chips && (
                            <ul
                                className="mt-7 flex flex-wrap gap-2"
                                data-testid={`gouter-chips-${section.id}`}
                            >
                                {section.chips.map((c) => {
                                    const CIcon = c.icon;
                                    return (
                                        <li
                                            key={c.label}
                                            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-arl-blue/10 text-sm text-arl-ink/75"
                                        >
                                            {CIcon && <CIcon size={14} className="text-arl-blue/70" />}
                                            <span>{c.label}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function Gouter() {
    return (
        <div data-testid="gouter-page">
            {/* HERO */}
            <section className="relative overflow-hidden pt-12 pb-12 sm:pt-16 sm:pb-16">
                <div className="absolute inset-0 rhombus-bg opacity-60 pointer-events-none" />

                <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
                    <h1
                        className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-arl-ink"
                        data-testid="gouter-title"
                    >
                        L'heure du goûter,
                        <br />
                        <span className="font-script text-arl-blue text-6xl sm:text-7xl lg:text-[5rem] inline-block leading-none">
                            comme à la maison.
                        </span>
                    </h1>
                    <p className="mt-6 text-lg text-arl-ink/75 max-w-2xl mx-auto leading-relaxed">
                        Un café, un cookie tout juste sorti, une gaufre dorée, une gousse de vanille à
                        glisser dans votre prochaine recette. Tout ce qu'il faut pour prolonger la pause.
                    </p>
                </div>
            </section>

            {sections.map((s, i) => (
                <GouterSection key={s.id} section={s} index={i} />
            ))}
        </div>
    );
}
